if (!customElements.get('collection-load-more')) {
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();

      history.scrollRestoration = 'manual';

      this.btnLoadMore = this.querySelector('.js-btn-load-more'); /* check */
      this.productGrid = document.querySelector('#product-grid'); /* check */
      this.storageKey = this.getStorageKey();

      if (this.isBackForwardNavigation()) {
        this.restoreFromCache();
      }

      this.setProperties();
      this.toggleProductsVisibility();

      window.addEventListener('shopify:section:load', () => {
        this.setProperties();
        this.toggleProductsVisibility();
      });

      // Safety net for real-device back navigation (e.g. iOS Safari swipe-back).
      // event.persisted is the browser's own authoritative signal for a bfcache
      // thaw and is far more reliably reported across engines than reading
      // performance.getEntriesByType('navigation') - which is what the
      // constructor-time check above relies on, and which some WebKit versions
      // don't populate the same way for restored pages. Falling back to that
      // check too, in case this pageshow is a non-bfcache reload instead.
      window.addEventListener('pageshow', event => {
        if (event.persisted || this.isBackForwardNavigation()) {
          this.restoreFromCache();
        }
      });

      this.btnLoadMore.addEventListener('click', e => {
        e.preventDefault();

        this.btnLoadMore.setAttribute('disabled', true);

        this.loadMoreProducts();
      });

      if (this.cardText) {
        window.addEventListener('resize', () => {
          if (DeviceDetector.isMobile()) {
            this.resetHiddenProducts();
            return;
          }
          this.toggleProductsVisibility();
        });
      }

      this.bindScrollPersistence();
    }

    isBackForwardNavigation() {
      const [entry] = performance.getEntriesByType('navigation');
      return entry?.type === 'back_forward';
    }

    getStorageKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return `collection-load-more-state:${url.pathname}${url.search}`;
    }

    readCache() {
      try {
        return JSON.parse(sessionStorage.getItem(this.storageKey)) || {};
      } catch (error) {
        return {};
      }
    }

    restoreFromCache() {
      const cached = this.readCache();

      if (cached.gridHTML && this.productGrid) {
        this.productGrid.innerHTML = cached.gridHTML;

        if (this.btnLoadMore && cached.loadMoreHref) {
          this.btnLoadMore.dataset.href = cached.loadMoreHref;
        }

        this.classList.toggle('hidden', !!cached.isLastPage);
      }

      if (typeof cached.scrollY === 'number') {
        this.scrollToWithRetry(cached.scrollY);
      }
    }

    // A single requestAnimationFrame isn't always enough: if the grid is still
    // shorter than the target scroll position (e.g. images haven't finished
    // reflowing yet), scrollTo silently clamps short and the page reads as
    // "reset to top" even though restoration did run. Keep re-applying for a
    // few frames until the page is tall enough to actually reach the target,
    // or until we give up after ~1s.
    scrollToWithRetry(targetY, attempt = 0) {
      window.scrollTo(0, targetY);

      const reached = window.scrollY >= targetY - 2;

      if (reached || attempt >= 30) {
        return;
      }

      requestAnimationFrame(() => {
        this.scrollToWithRetry(targetY, attempt + 1);
      });
    }

    saveGridToCache() {
      if (!this.productGrid) return;

      const cached = this.readCache();

      cached.gridHTML = this.productGrid.innerHTML;
      cached.loadMoreHref = this.btnLoadMore?.dataset.href || '';
      cached.isLastPage = this.classList.contains('hidden');

      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(cached));
      } catch (error) {
        // sessionStorage unavailable or full - restoration simply won't apply
      }
    }

    saveScrollToCache() {
      const cached = this.readCache();

      cached.scrollY = window.scrollY;

      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(cached));
      } catch (error) {
        // sessionStorage unavailable or full - restoration simply won't apply
      }
    }

    bindScrollPersistence() {
      // The browser can already reset window.scrollY to 0 on the outgoing
      // page before "pagehide"/"beforeunload" ever fire, so waiting for an
      // unload-time event to save the scroll position is too late - it
      // captures the already-reset 0, not the real position. Saving at click
      // time (capture phase, so this runs before the link's own navigation
      // starts) is the only point that's guaranteed to still see the correct
      // scrollY.
      document.addEventListener(
        'click',
        event => {
          if (event.target.closest('a[href*="/products/"]')) {
            this.saveScrollToCache();
          }
        },
        true
      );

      let ticking = false;

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
          this.saveScrollToCache();
          ticking = false;
        });
      });
    }

    setProperties() {
      this.productGrid = document.querySelector('#product-grid');
      this.cardText = this.productGrid.querySelector('.js-card-text');
      this.btnLoadMore = this.querySelector('.js-btn-load-more');
      this.hiddenProducts = [];
      this.cardTextIndexOffset = 2;
      this.isLastPage = this.classList.contains('hidden');
      this.cardProduct = document.querySelector('card-product');
      this.setGridColsNumber();
    }

    setGridColsNumber() {
      // prettier-ignore
      this.productGridCols = Math.round(this.productGrid.getBoundingClientRect().width / this.cardProduct?.getBoundingClientRect().width);
    }

    toggleProductsVisibility() {
      if (!this.cardText || this.isLastPage) {
        return;
      }

      const items = Array.from(this.productGrid.children);
      const visibleItems = items
        .filter(item => !item.classList.contains('hidden'))
        .reverse();
      const productRect = visibleItems
        .find(item => !item.classList.contains('js-card-text'))
        .getBoundingClientRect();
      const lastItem = visibleItems[0];

      const productGridRect =
        this.productGrid.getBoundingClientRect();
      const lastItemRect = lastItem.getBoundingClientRect();

      const productGridCols = Math.round(
        productGridRect.width / productRect.width
      );
      const productGridOffset =
        productGridRect.x + productGridRect.width;
      const lastItemOffset = lastItemRect.x + lastItemRect.width;

      const isLastItemCardText =
        items[items.length - 1].classList.contains('js-card-text');

      if (isLastItemCardText) {
        const prevProduct = visibleItems[1];
        const prevProductRect = prevProduct.getBoundingClientRect();
        const prevProductOffset =
          prevProductRect.x + prevProductRect.width;

        if (
          lastItemRect.y !== prevProductRect.y &&
          productGridOffset !== prevProductOffset
        ) {
          this.hiddenProducts[0].classList.remove('hidden');
          this.hiddenProducts.shift();
        }
      }

      const isLastColumn = productGridOffset === lastItemOffset;

      if (isLastColumn) {
        if (this.hiddenProducts.length >= productGridCols) {
          this.resetHiddenProducts(productGridCols);
        }

        return;
      }

      // prettier-ignore
      const gridGaps = Math.round((productGridRect.width - (lastItemOffset - productGridRect.x)) / productRect.width);

      if (this.hiddenProducts.length >= gridGaps) {
        const hiddenProductsLimit =
          this.hiddenProducts.length - gridGaps;

        this.resetHiddenProducts(hiddenProductsLimit);

        return;
      }

      let productsLooped = 0;

      for (const product of visibleItems) {
        if (product.classList.contains('js-card-text')) {
          continue;
        }

        productsLooped++;
        this.hiddenProducts.unshift(product);
        product.classList.add('hidden');

        if (productsLooped === productGridCols - gridGaps) {
          break;
        }
      }
    }

    resetHiddenProducts(limit) {
      if (!this.cardText) {
        return;
      }

      const hiddenProducts = [...this.hiddenProducts];
      let i = 0;

      for (const product of hiddenProducts) {
        i++;

        product.classList.remove('hidden');
        this.hiddenProducts.shift();

        if (limit && limit === i) {
          break;
        }
      }
    }

    async loadMoreProducts() {
      try {
        const nextPageResponse = await fetch(
          `${this.btnLoadMore.dataset.href}`
        );
        const nextPageText = await nextPageResponse.text();

        const nextPageHTML = new DOMParser().parseFromString(
          nextPageText,
          'text/html'
        );

        const productGridContainer = nextPageHTML.querySelector(
          '#ProductGridContainer'
        );
        const productGrid =
          productGridContainer.querySelector('#product-grid');
        const loadMoreContainer = productGridContainer.querySelector(
          'collection-load-more'
        );
        const btnLoadMore = loadMoreContainer.querySelector(
          '.js-btn-load-more'
        );

        const isLoadMoreHidden =
          loadMoreContainer.classList.contains('hidden');
        const products = productGrid.children;

        if (this.cardText) {
          if (this.cardTextIndexOffset > 0) {
            const productsArray = Array.from(products);
            const cardTextIndex = productsArray.findIndex(product =>
              product.classList.contains('js-card-text')
            );
            const elementAfterCardText =
              products[cardTextIndex - this.cardTextIndexOffset];

            this.cardTextIndexOffset = 0;

            if (elementAfterCardText) {
              const cardText = productsArray[cardTextIndex];

              cardText.remove();
              elementAfterCardText.insertAdjacentElement(
                'beforebegin',
                cardText
              );
            }
          } else {
            this.cardTextIndexOffset = 2;
          }
        }

        this.resetHiddenProducts();
        history.replaceState({}, '', this.btnLoadMore.dataset.href);
        this.productGrid.insertAdjacentHTML(
          'beforeend',
          productGrid.innerHTML
        );

        if (isLoadMoreHidden) {
          this.isLastPage = true;
          this.classList.add('hidden');

          return;
        }

        this.toggleProductsVisibility();
        this.btnLoadMore.dataset.href = btnLoadMore.dataset.href;
      } catch (error) {
        console.error(error);

        alert(error.message);
      } finally {
        this.btnLoadMore.removeAttribute('disabled');
        this.saveGridToCache();
      }
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
}
